import React from 'react';
import { useAuth } from '../contexts/AuthContext';
import { AuthModal } from '../components/auth/AuthModal';
import { VerseOfDay } from '../components/home/VerseOfDay';
import { FeatureCard } from '../components/home/FeatureCard';
import { motion, useScroll, useTransform } from 'framer-motion';
import { useNavigate } from 'react-router-dom';
import { BookOpen, Heart, Sparkles, Brain, Calendar, Phone, Search, MessageSquare } from 'lucide-react';

const FEATURES = [
  {
    to: '/verses',
    icon: Search,
    title: 'Verse Finder',
    description: 'Search and discover verses that speak to your heart',
    image: 'https://images.unsplash.com/photo-1505051508008-923feaf90180?w=800&q=80',
    requiresAuth: true
  },
  {
    to: '/trivia',
    icon: Brain,
    title: 'Bible Trivia',
    description: 'Challenge yourself with biblical knowledge quizzes',
    image: 'https://images.unsplash.com/photo-1528605105345-5344ea20e269?w=800&q=80',
    requiresAuth: true
  },
  {
    to: '/daily-motivation',
    icon: Sparkles,
    title: 'Daily Motivation',
    description: 'Start your day with inspiring verses and reflections',
    image: 'https://images.unsplash.com/photo-1500964757637-c85e8a162699?w=800&q=80',
    requiresAuth: false
  },
  {
    to: '/prayer-room',
    icon: Heart,
    title: 'Prayer Room',
    description: 'Share prayer requests and intercede for others',
    image: 'https://images.unsplash.com/photo-1438232992991-995b7058bbb3?w=800&q=80',
    requiresAuth: false
  },
  {
    to: '/pray-4-me',
    icon: Phone,
    title: 'Pray 4 Me',
    description: 'Connect with AI-powered prayer support',
    image: 'https://images.unsplash.com/photo-1515634928627-2a4e0dae3ddf?w=800&q=80',
    requiresAuth: false
  },
  {
    to: '/monthly-readings',
    icon: Calendar,
    title: 'Monthly Readings',
    description: 'Follow structured Bible reading plans',
    image: 'https://images.unsplash.com/photo-1484480974693-6ca0a78fb36b?w=800&q=80',
    requiresAuth: false
  },
  {
    to: '/bible',
    icon: BookOpen,
    title: 'Bible Study',
    description: 'Read and explore the Bible in multiple translations',
    image: 'https://images.unsplash.com/photo-1519491050282-cf00c82424b4?w=800&q=80',
    requiresAuth: false
  },
  {
    to: '/contact',
    icon: MessageSquare,
    title: 'Get in Touch',
    description: 'Reach out with questions or feedback',
    image: 'https://images.unsplash.com/photo-1502691876148-a84978e59af8?w=800&q=80',
    requiresAuth: false
  }
];

export default function Home() {
  const { user } = useAuth();
  const [showAuth, setShowAuth] = React.useState(false);
  const navigate = useNavigate();

  const { scrollYProgress } = useScroll();
  const y1 = useTransform(scrollYProgress, [0, 1], [0, -200]);
  const y2 = useTransform(scrollYProgress, [0, 1], [0, -400]);
  const y3 = useTransform(scrollYProgress, [0, 1], [0, -100]);
  const opacity = useTransform(scrollYProgress, [0, 0.5], [1, 0]);
  const scale = useTransform(scrollYProgress, [0, 0.5], [1, 0.8]);

  const handleFeatureClick = (to: string, requiresAuth: boolean) => {
    if (requiresAuth && !user) {
      setShowAuth(true);
    } else {
      navigate(to);
    }
  };

  return (
    <div className="relative min-h-screen overflow-x-hidden">
      {showAuth && !user && <AuthModal onClose={() => setShowAuth(false)} />}

      {/* Hero Section with Parallax */}
      <div className={`relative ${user ? 'min-h-[30vh] md:min-h-[40vh]' : 'min-h-[70vh]'} flex items-center justify-center overflow-hidden`}>
        {/* Layered Background Images with Parallax */}
        <motion.div
          style={{ y: y2 }}
          className="absolute inset-0 z-0"
        >
          <div
            className="absolute inset-0 opacity-20 dark:opacity-10 bg-cover bg-center"
            style={{
              backgroundImage: `url('https://images.unsplash.com/photo-1519491050282-cf00c82424b4?w=1600&q=80')`,
              filter: 'blur(3px)'
            }}
          />
          <div className="absolute inset-0 bg-gradient-to-b from-purple-100/60 via-white/80 to-white dark:from-purple-950/60 dark:via-gray-900/80 dark:to-gray-900" />
        </motion.div>

        {/* Floating Orbs with Parallax */}
        <motion.div
          style={{ y: y1 }}
          className="absolute top-20 left-[10%] w-72 h-72 bg-gradient-to-br from-purple-400/30 to-fuchsia-400/30 rounded-full blur-3xl"
        />
        <motion.div
          style={{ y: y3 }}
          className="absolute bottom-32 right-[15%] w-96 h-96 bg-gradient-to-br from-violet-400/20 to-purple-400/20 rounded-full blur-3xl"
        />
        <motion.div
          style={{ y: y2 }}
          className="absolute top-1/2 left-1/2 -translate-x-1/2 -translate-y-1/2 w-[600px] h-[600px] bg-gradient-to-br from-fuchsia-300/10 to-purple-300/10 rounded-full blur-3xl"
        />

        {/* Hero Content */}
        <motion.div
          style={{ opacity, scale }}
          className="relative z-10 px-4 sm:px-6 lg:px-8 py-8 max-w-6xl mx-auto"
        >
          {user ? (
            <motion.div
              initial={{ opacity: 0, y: 30 }}
              animate={{ opacity: 1, y: 0 }}
              className="w-full mb-4 md:mb-8"
            >
              <VerseOfDay />
            </motion.div>
          ) : (
            <div className="text-center">
              <motion.div
                initial={{ opacity: 0, y: 40 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ duration: 1, ease: [0.16, 1, 0.3, 1] }}
                className="mb-8"
              >
                <motion.div
                  animate={{
                    rotate: [0, 5, -5, 0],
                    scale: [1, 1.05, 1]
                  }}
                  transition={{
                    duration: 6,
                    repeat: Infinity,
                    ease: "easeInOut"
                  }}
                  className="inline-block mb-6"
                >
                  <div className="relative">
                    <div className="absolute -inset-4 bg-gradient-to-r from-purple-600 to-fuchsia-600 rounded-full opacity-20 blur-2xl" />
                    <span className="relative text-6xl sm:text-7xl md:text-8xl">✝️</span>
                  </div>
                </motion.div>

                <h1 className="text-5xl sm:text-6xl md:text-7xl lg:text-8xl font-black text-gray-900 dark:text-white mb-6 leading-[1.1]">
                  Walk with
                  <span className="block premium-text mt-2">God Daily</span>
                </h1>
              </motion.div>

              <motion.p
                initial={{ opacity: 0, y: 30 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ delay: 0.2, duration: 0.8 }}
                className="text-xl sm:text-2xl text-gray-700 dark:text-gray-300 mb-12 max-w-3xl mx-auto leading-relaxed"
              >
                Experience the transformative power of scripture through daily readings,
                engaging trivia, and a supportive community of faith.
              </motion.p>

              <motion.div
                initial={{ opacity: 0, y: 30 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ delay: 0.4, duration: 0.8 }}
                className="flex flex-col sm:flex-row gap-4 justify-center"
              >
                <button
                  onClick={() => setShowAuth(true)}
                  className="group px-10 py-5 premium-button text-white rounded-2xl font-bold text-lg inline-flex items-center justify-center gap-3 hover:gap-4 transition-all"
                >
                  Begin Your Journey
                  <Sparkles className="w-5 h-5" />
                </button>
                <button
                  onClick={() => navigate('/landing')}
                  className="px-10 py-5 bg-white dark:bg-gray-800 text-purple-600 dark:text-purple-400 rounded-2xl font-bold text-lg border-2 border-purple-200 dark:border-purple-700 hover:border-purple-400 dark:hover:border-purple-500 transition-all hover:shadow-lg"
                >
                  Learn More
                </button>
              </motion.div>
            </div>
          )}
        </motion.div>
      </div>

      {/* Features Grid */}
      <div className="relative py-24 px-4 sm:px-6 lg:px-8 bg-gradient-to-b from-white via-purple-50/30 to-white dark:from-gray-900 dark:via-purple-950/30 dark:to-gray-900">
        {/* Decorative Elements */}
        <div className="absolute inset-0 overflow-hidden pointer-events-none">
          <div className="absolute top-1/4 -left-20 w-72 h-72 bg-purple-300/20 dark:bg-purple-600/10 rounded-full blur-3xl" />
          <div className="absolute bottom-1/4 -right-20 w-72 h-72 bg-fuchsia-300/20 dark:bg-fuchsia-600/10 rounded-full blur-3xl" />
        </div>

        <div className="relative max-w-7xl mx-auto">
          <motion.div
            initial={{ opacity: 0, y: 40 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true, margin: "-100px" }}
            transition={{ duration: 0.8 }}
            className="text-center mb-20"
          >
            <h2 className="text-4xl sm:text-5xl md:text-6xl font-black text-gray-900 dark:text-white mb-6">
              Explore Our
              <span className="block premium-text">Features</span>
            </h2>
            <p className="text-xl text-gray-600 dark:text-gray-400 max-w-3xl mx-auto">
              Everything you need for a deeper connection with scripture and community
            </p>
          </motion.div>

          <div className="grid grid-cols-2 md:grid-cols-2 lg:grid-cols-4 gap-4 sm:gap-6">
            {FEATURES.map((feature, index) => (
              <motion.div
                key={feature.to}
                initial={{ opacity: 0, y: 50 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true, margin: "-50px" }}
                transition={{
                  duration: 0.6,
                  delay: index * 0.1,
                  ease: [0.16, 1, 0.3, 1]
                }}
                whileHover={{ y: -8, transition: { duration: 0.2 } }}
                onClick={() => handleFeatureClick(feature.to, feature.requiresAuth)}
                className="group cursor-pointer"
              >
                <div className="relative h-full rounded-2xl sm:rounded-3xl overflow-hidden bg-white dark:bg-gray-900 border border-purple-200/50 dark:border-purple-700/50 shadow-lg hover:shadow-2xl hover:shadow-purple-500/20 transition-all duration-500">
                  {/* Image */}
                  <div className="relative h-32 sm:h-48 overflow-hidden">
                    <div
                      className="absolute inset-0 bg-cover bg-center transform group-hover:scale-110 transition-transform duration-700"
                      style={{ backgroundImage: `url('${feature.image}')` }}
                    />
                    <div className="absolute inset-0 bg-gradient-to-t from-black/60 via-black/20 to-transparent" />
                    <div className="absolute bottom-2 left-2 sm:bottom-4 sm:left-4 w-10 h-10 sm:w-12 sm:h-12 rounded-lg sm:rounded-xl bg-purple-600/90 backdrop-blur-sm flex items-center justify-center">
                      <feature.icon className="w-5 h-5 sm:w-6 sm:h-6 text-white" />
                    </div>
                  </div>

                  {/* Content */}
                  <div className="p-3 sm:p-6">
                    <h3 className="text-sm sm:text-xl font-bold text-gray-900 dark:text-white mb-1 sm:mb-2 group-hover:text-purple-600 dark:group-hover:text-purple-400 transition-colors line-clamp-1">
                      {feature.title}
                    </h3>
                    <p className="text-gray-600 dark:text-gray-400 text-xs sm:text-sm leading-relaxed line-clamp-2">
                      {feature.description}
                    </p>
                  </div>

                  {/* Hover Gradient Overlay */}
                  <div className="absolute inset-0 bg-gradient-to-br from-purple-500/0 via-violet-500/0 to-fuchsia-500/0 group-hover:from-purple-500/5 group-hover:via-violet-500/5 group-hover:to-fuchsia-500/5 transition-all duration-500 pointer-events-none" />
                </div>
              </motion.div>
            ))}
          </div>
        </div>
      </div>

      {/* CTA Section */}
      {!user && (
        <div className="relative py-32 px-4 sm:px-6 lg:px-8 overflow-hidden">
          {/* Background */}
          <div className="absolute inset-0">
            <div
              className="absolute inset-0 opacity-10 dark:opacity-5 bg-cover bg-center"
              style={{ backgroundImage: `url('https://images.unsplash.com/photo-1502691876148-a84978e59af8?w=1600&q=80')` }}
            />
            <div className="absolute inset-0 bg-gradient-to-br from-purple-600/20 via-violet-600/20 to-fuchsia-600/20" />
          </div>

          <motion.div
            initial={{ opacity: 0, y: 40 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 0.8 }}
            className="relative max-w-4xl mx-auto text-center"
          >
            <h2 className="text-4xl sm:text-5xl md:text-6xl font-black text-gray-900 dark:text-white mb-6">
              Ready to Deepen
              <span className="block premium-text mt-2">Your Faith?</span>
            </h2>
            <p className="text-xl text-gray-700 dark:text-gray-300 mb-10 max-w-2xl mx-auto">
              Join thousands discovering the joy of daily scripture and community support
            </p>
            <button
              onClick={() => setShowAuth(true)}
              className="px-12 py-6 premium-button text-white rounded-2xl font-bold text-xl hover:scale-105 transition-transform"
            >
              Get Started Free
            </button>
          </motion.div>
        </div>
      )}
    </div>
  );
}
